import React, { PropsWithoutRef, useState } from 'react';

import {
  Autocomplete,
  Checkbox,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Stack,
  TextField,
  autocompleteClasses,
} from '@mui/material';
import { User } from 'ts_shared';
import { useDebouncedValue } from '@mantine/hooks';
import { isEqual, uniq } from 'lodash';

import { FormFieldDescription } from 'components/common/Forms';
import ControllerWithError from 'libs/react-hook-form/ControllerWithError';
import { ResourceFormProps } from 'types';
import {
  SitesGroupByCompanyResponseRead,
  useSitesGroupByCompanyRetrieveQuery,
} from 'services/api/general';
import { isEverythingSelected, isSomeSelected } from 'utils/arrayComparisons';
import useUserFormContext from '../../useUserFormContext';

type CheckboxState = 'checked' | 'unchecked' | 'indeterminate';

const groupSiteData = (data?: SitesGroupByCompanyResponseRead) => {
  return data?.companies
    .map((company) =>
      company.sites.map((site) => ({
        ...site,
        companyName: company.name,
        companyId: company.id,
      })),
    )
    .flat();
};

const ExternalUserSitesDropdown = ({
  control,
}: PropsWithoutRef<ResourceFormProps<User>>) => {
  const [siteNameContains, setSiteNameContains] = useState('');
  const [debouncedSiteNameContains] = useDebouncedValue(siteNameContains, 500);

  const { data: rawSitesData } = useSitesGroupByCompanyRetrieveQuery({});
  const allSites = groupSiteData(rawSitesData);
  const sitesGroupedByCompany = rawSitesData?.companies;

  const { data: filteredSites, isFetching } =
    useSitesGroupByCompanyRetrieveQuery(
      { siteNameContains: debouncedSiteNameContains },
      {
        selectFromResult: ({ data, ...rest }) => ({
          ...rest,
          data: groupSiteData(data),
        }),
      },
    );

  const { watch, setValue } = useUserFormContext();
  const selectedSites = watch('sites');

  const handleCompanyOptionClick = (
    companyId: string,
    checkboxState: CheckboxState,
  ) => {
    const clickedCompanySites =
      sitesGroupedByCompany
        ?.find((company) => company.id === companyId)
        ?.sites.map(({ id }) => id) || [];

    if (checkboxState === 'unchecked') {
      const newSites = selectedSites?.concat(clickedCompanySites);
      setValue('sites', uniq(newSites), { shouldDirty: true });
    } else {
      const remainingSites = selectedSites?.filter(
        (siteId) => !clickedCompanySites.includes(siteId),
      );
      setValue('sites', remainingSites, { shouldDirty: true });
    }
  };

  return (
    <Stack>
      <FormFieldDescription
        title="User Sites"
        subtitle="Sites that the user can access"
        required
      />
      <ControllerWithError
        control={control}
        name="sites"
        render={({ field: { onChange, value } }) => (
          <Autocomplete
            multiple
            disableCloseOnSelect
            inputValue={siteNameContains}
            renderInput={(params) => (
              <TextField
                {...params}
                sx={{ borderRadius: 2, minHeight: '50px' }}
                size="small"
                onChange={(e) => setSiteNameContains(e.target.value)}
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isFetching ? <CircularProgress size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  },
                }}
              />
            )}
            options={filteredSites || []}
            filterOptions={(options) => {
              // Prevents any filtering from happening by the Autocomplete component.
              // We do the filtering with the endpoint by updating the provided options directly.
              return options;
            }}
            value={allSites?.filter((site) => value?.includes(site.id)) || []}
            onChange={(_, newSites) =>
              onChange(newSites.map((site) => site.id))
            }
            isOptionEqualToValue={(option, inputVal) =>
              isEqual(option, inputVal)
            }
            slotProps={{
              popper: {
                sx: {
                  boxShadow:
                    // same shadow value as other dropdown in User Form (User group)
                    '0px 5px 5px -3px rgba(0, 0, 0, 0.2), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 3px 14px 2px rgba(0, 0, 0, 0.12)',
                },
              },
            }}
            groupBy={(option) => option.companyId}
            getOptionLabel={(option) => option.name}
            renderOption={(props, option, state) => {
              return (
                <ListItem
                  {...props}
                  sx={{
                    [`&.${autocompleteClasses.option}`]: {
                      padding: 0,
                    },
                  }}
                >
                  <ListItemButton sx={{ padding: 0 }}>
                    <ListItemIcon sx={{ minHeight: '30px', minWidth: '30px' }}>
                      <Checkbox
                        checked={state.selected}
                        size="small"
                        disableRipple
                      />
                    </ListItemIcon>
                    {option.name}
                  </ListItemButton>
                </ListItem>
              );
            }}
            renderGroup={(params) => {
              const companyId = params.group;
              const companyName = allSites?.find(
                (site) => site.companyId === params.group,
              )?.companyName;

              const companySites =
                sitesGroupedByCompany
                  ?.find((company) => company.id === companyId)
                  ?.sites.map((site) => site.id) || [];

              const isAllCompanySitesSelected = isEverythingSelected(
                companySites,
                value || [],
              );
              const isSomeCompanySitesSelected = isSomeSelected(
                value || [],
                companySites,
              );

              let checkboxState: CheckboxState;

              if (isAllCompanySitesSelected) {
                checkboxState = 'checked';
              } else if (isSomeCompanySitesSelected) {
                checkboxState = 'indeterminate';
              } else {
                checkboxState = 'unchecked';
              }

              return (
                <>
                  <ListItemButton
                    sx={{
                      height: '48px',
                      fontWeight: 'bold',
                      fontSize: '0.875rem',
                      color: 'text.primary',
                    }}
                    onClick={() =>
                      handleCompanyOptionClick(companyId, checkboxState)
                    }
                  >
                    <ListItemIcon sx={{ minHeight: '30px', minWidth: '30px' }}>
                      <Checkbox
                        checked={checkboxState === 'checked'}
                        indeterminate={checkboxState === 'indeterminate'}
                        size="small"
                        disableRipple
                      />
                    </ListItemIcon>
                    {companyName}
                  </ListItemButton>
                  <List sx={{ pl: 5 }} disablePadding>
                    {params.children}
                  </List>
                </>
              );
            }}
          />
        )}
      />
    </Stack>
  );
};

export default ExternalUserSitesDropdown;
